import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { ArrowLeft, MessageCircle, ShoppingBag, Package, Clock, Search, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price, currency = 'IDR') => {
  if (currency === 'IDR') return `Rp ${Number(price).toLocaleString('id-ID')}`;
  return `${currency} ${Number(price).toLocaleString()}`;
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
}

function ConversationCard({ conv, onClick }) {
  const { other_user, context, last_message, last_message_at, unread_count, type, is_blocked, blocked_other, is_closed } = conv;
  const isAdminChannel = other_user?.is_admin_channel;
  const photo = context?.photo || null;
  const productName = isAdminChannel
    ? 'Admin message'
    : (context?.product_name || (type === 'order' ? `Order #${context?.order_id || ''}` : 'Conversation'));
  const isBlocked = is_blocked || blocked_other;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-2xl p-4 border transition-all hover:shadow-sm ${
        unread_count > 0 ? 'border-[#F4C2C2] ring-1 ring-[#F4C2C2]/30' : 'border-cream-100/50'
      } ${isBlocked ? 'opacity-60' : ''} ${isAdminChannel ? 'bg-gradient-to-br from-purple-50/40 to-white' : ''}`}
      data-testid={`conversation-${conv.conversation_id}`}
    >
      <div className="flex gap-3">
        {/* Product/order/admin thumbnail */}
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center ${isAdminChannel ? 'bg-purple-100' : 'bg-cream-50'}`}>
          {isAdminChannel ? (
            <ShieldCheck size={20} className="text-purple-600" />
          ) : photo ? (
            <img src={photo} alt="" className="w-full h-full object-cover" />
          ) : (
            type === 'order' ? <Package size={18} className="text-[#A89F91]" /> : <ShoppingBag size={18} className="text-[#A89F91]" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={`font-body text-sm truncate flex items-center gap-1 ${unread_count > 0 ? 'font-bold text-[#2C2A29]' : 'font-semibold text-[#2C2A29]'}`}>
              {other_user?.name || 'Unknown'}
              {isAdminChannel && <ShieldCheck size={12} className="text-purple-600 flex-shrink-0" />}
            </p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {last_message_at && (
                <span className="text-[11px] font-body text-[#A89F91]">{timeAgo(last_message_at)}</span>
              )}
              {unread_count > 0 && (
                <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-[#D48585] text-white text-[10px] font-bold rounded-full px-1" data-testid={`unread-badge-${conv.conversation_id}`}>
                  {unread_count > 9 ? '9+' : unread_count}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-body font-semibold uppercase tracking-wider ${
              isAdminChannel ? 'bg-purple-100 text-purple-700' :
              type === 'listing' ? 'bg-[#FFF5F5] text-[#D48585]' : 'bg-blue-50 text-blue-600'
            }`}>
              {isAdminChannel ? 'Official' : type === 'listing' ? 'Listing' : type === 'admin_dm' ? 'DM' : 'Order'}
            </span>
            <p className="text-xs font-body text-[#78716C] truncate">{productName}</p>
            {is_closed && <span className="text-[10px] font-body text-[#A89F91] italic">· closed</span>}
          </div>

          {last_message && (
            <p className={`text-xs mt-1 truncate ${unread_count > 0 ? 'font-medium text-[#2C2A29]' : 'font-body text-[#A89F91]'}`}>
              {last_message}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

export default function InboxPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [adminSearchOpen, setAdminSearchOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    axios.get(`${API}/messages/inbox`)
      .then(r => setConversations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated, authLoading]);

  // Poll every 15s + immediate refetch on tab visible / window focus
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    const fetchInbox = () => {
      axios.get(`${API}/messages/inbox`)
        .then(r => setConversations(r.data))
        .catch(() => {});
    };
    const interval = setInterval(fetchInbox, 15000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchInbox(); };
    const onFocus = () => fetchInbox();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated, authLoading]);

  // Debounced admin search
  useEffect(() => {
    if (!user?.is_admin) return;
    const q = adminSearch.trim();
    if (q.length < 2) { setAdminSearchResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/admin/users/search`, { params: { q } })
        .then(r => setAdminSearchResults(r.data || []))
        .catch(err => console.warn('[InboxPage] admin search failed:', err?.response?.status));
    }, 250);
    return () => clearTimeout(t);
  }, [adminSearch, user?.is_admin]);

  const openAdminDm = async (targetUserId) => {
    try {
      const { data } = await axios.post(`${API}/admin/messages/open`, { user_id: targetUserId });
      navigate(`/messages/${data.conversation_id}`);
    } catch (err) {
      console.warn('[InboxPage] admin open DM failed:', err?.response?.data || err?.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <button onClick={() => navigate('/')} className="flex items-center gap-1 text-sm font-body text-[#78716C] hover:text-[#2C2A29] mb-4" data-testid="inbox-back">
          <ArrowLeft size={16} /> Back
        </button>

        <h1 className="font-heading text-3xl text-[#2C2A29] mb-6" data-testid="inbox-title">Messages</h1>

        {/* Admin-only: DM any user */}
        {user?.is_admin && (
          <div className="mb-5 bg-purple-50/60 border border-purple-100 rounded-2xl p-3" data-testid="admin-dm-search-card">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={14} className="text-purple-600" />
              <span className="text-xs font-body font-semibold text-purple-800 uppercase tracking-wider">Admin — DM any user</span>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A89F91]" />
              <input
                type="text"
                value={adminSearch}
                onChange={e => { setAdminSearch(e.target.value); setAdminSearchOpen(true); }}
                onFocus={() => setAdminSearchOpen(true)}
                placeholder="Search by username, email, or display name…"
                className="w-full bg-white border border-cream-100 rounded-xl pl-9 pr-3 py-2.5 font-body text-sm text-[#2C2A29] placeholder:text-[#A89F91] focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-transparent"
                data-testid="admin-dm-search-input"
              />
              {adminSearchOpen && adminSearch.trim().length >= 2 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white rounded-xl border border-cream-100 shadow-lg max-h-72 overflow-y-auto" data-testid="admin-dm-search-results">
                  {adminSearchResults.length === 0 ? (
                    <p className="text-xs font-body text-[#A89F91] px-3 py-4 text-center">No matches</p>
                  ) : adminSearchResults.map(u => (
                    <button
                      key={u.user_id}
                      onClick={() => { setAdminSearchOpen(false); setAdminSearch(''); openAdminDm(u.user_id); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-purple-50/50 flex items-center gap-2 border-b border-cream-100/50 last:border-b-0"
                      data-testid={`admin-dm-result-${u.user_id}`}
                    >
                      {u.avatar ? (
                        <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-cream-100 flex items-center justify-center flex-shrink-0">
                          <MessageCircle size={14} className="text-cream-300" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-body text-sm font-medium text-[#2C2A29] truncate">{u.display_name || u.username || u.email}</p>
                        <p className="font-body text-xs text-[#78716C] truncate">@{u.username || u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-20 bg-cream-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : !isAuthenticated ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="inbox-login-prompt">
            <MessageCircle size={40} className="mx-auto text-cream-200 mb-3" />
            <p className="font-body text-[#78716C] mb-2">Sign in to see your messages</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="inbox-empty">
            <MessageCircle size={40} className="mx-auto text-cream-200 mb-3" />
            <p className="font-body text-[#78716C] mb-2">No messages yet</p>
            <p className="font-body text-sm text-[#A89F91]">Start a conversation by messaging a seller from any listing</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="inbox-list">
            {conversations.map(conv => (
              <ConversationCard
                key={conv.conversation_id}
                conv={conv}
                onClick={() => navigate(`/messages/${conv.conversation_id}`)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
